/** Sidebar with species, month, and maintenance-measure filters. */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";

import { ColorWheelPicker } from "./ColorWheelPicker";
import { SpeciesInfoPopover } from "./SpeciesInfoPopover";
import { ZOOM_BREAKPOINTS } from "../constants/mapZoom";
import { getMonthLabels } from "../constants/months";
import { useLanguageContext } from "../hooks/useLanguage";
import { useIsMobile } from "../hooks/useMediaQuery";
import type { FilterState, MapMode, RegionConfig } from "../types";

interface SidebarProps {
  config: RegionConfig;
  filters: FilterState;
  mode: MapMode;
  mapZoom: number;
  open: boolean;
  highlightedMeasureIds: Set<string>;
  lineOverlayMeasureId: string | null;
  presentMeasureIds: Set<string>;
  /** Whether any habitat with no permitted work is visible under the current
   * filter — controls the red-dashed "no work permitted" legend entry. */
  hasNoWorkAreas: boolean;
  /** Whether any "both species" habitat-overlap line is visible under the
   * current filter — controls the green overlap legend entry. */
  hasSpeciesOverlap: boolean;
  highlightNoWork: boolean;
  lineOverlayNoWork: boolean;
  onNoWorkHighlightToggle: () => void;
  onNoWorkOverlayToggle: () => void;
  onFilterChange: Dispatch<SetStateAction<FilterState>>;
  onLegendMeasureClick: (measureId: string) => void;
  onLineOverlayToggle: (measureId: string) => void;
  onMeasureColorChange: (measureId: string, color: string) => void;
  onResetMeasureColors: () => void;
  onClose: () => void;
}

const checkboxClassName =
  "h-4 w-4 shrink-0 rounded border-flima-spruce/25 text-flima-river [accent-color:#147D92] focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2";

type SidebarSection = "species" | "months" | "measures";

const sectionBodyIds: Record<SidebarSection, string> = {
  species: "sidebar-species-section",
  months: "sidebar-months-section",
  measures: "sidebar-measures-section",
};

function formatMonthSummary(months: Set<number>, labels: string[], allLabel: string): string {
  const sorted = [...months]
    .filter((month) => month >= 1 && month <= 12)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return "—";
  if (sorted.length === 12) return allLabel;

  const parts: string[] = [];
  let runStart = sorted[0];
  let previous = sorted[0];

  const closeRun = (start: number, end: number) => {
    parts.push(start === end ? labels[start - 1] : `${labels[start - 1]}–${labels[end - 1]}`);
  };

  for (let index = 1; index <= sorted.length; index++) {
    const current = index < sorted.length ? sorted[index] : undefined;
    if (current !== undefined && current === previous + 1) {
      previous = current;
      continue;
    }
    closeRun(runStart, previous);
    if (current !== undefined) {
      runStart = current;
      previous = current;
    }
  }

  return parts.join(", ");
}

export const Sidebar = memo(function Sidebar({
  config,
  filters,
  mode,
  mapZoom,
  open,
  highlightedMeasureIds,
  lineOverlayMeasureId,
  presentMeasureIds,
  hasNoWorkAreas,
  hasSpeciesOverlap,
  highlightNoWork,
  lineOverlayNoWork,
  onNoWorkHighlightToggle,
  onNoWorkOverlayToggle,
  onFilterChange,
  onLegendMeasureClick,
  onLineOverlayToggle,
  onMeasureColorChange,
  onResetMeasureColors,
  onClose,
}: SidebarProps) {
  const { t, lang } = useLanguageContext();
  const isMobile = useIsMobile();
  const asideRef = useRef<HTMLElement>(null);
  const monthLabels = getMonthLabels(lang);
  const [openSections, setOpenSections] = useState<Set<SidebarSection>>(
    () => new Set<SidebarSection>(mode === "planning" ? ["months", "measures"] : ["species"]),
  );
  const [legendInfoOpen, setLegendInfoOpen] = useState(false);

  /** Get the localised species name, falling back to the config name. */
  const speciesName = (speciesId: string, fallback: string): string => {
    const key = `species_${speciesId}`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  // When slid off-screen on mobile, mark the panel inert so its controls are not
  // tab-focusable. At md+ the panel is always docked, so it is never inert.
  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    if (isMobile && !open) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }, [isMobile, open]);

  useEffect(() => {
    setOpenSections(
      new Set<SidebarSection>(mode === "planning" ? ["months", "measures"] : ["species"]),
    );
  }, [mode]);

  const toggleSection = useCallback((section: SidebarSection) => {
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const visibleMeasures = useMemo(
    () =>
      config.measures.filter(
        (measure) =>
          (!measure.species || filters.species.has(measure.species)) &&
          measure.months.some((month) => filters.months.has(month)) &&
          presentMeasureIds.has(measure.id),
      ),
    [config.measures, filters.species, filters.months, presentMeasureIds],
  );

  const toggleSpecies = useCallback(
    (id: string) => {
      onFilterChange((previous) => {
        const species = new Set(previous.species);
        if (species.has(id)) species.delete(id);
        else species.add(id);
        return { ...previous, species };
      });
    },
    [onFilterChange],
  );

  const toggleMonth = useCallback(
    (month: number) => {
      onFilterChange((previous) => {
        const months = new Set(previous.months);
        if (months.has(month)) months.delete(month);
        else months.add(month);
        return { ...previous, months };
      });
    },
    [onFilterChange],
  );

  const selectAllMonths = useCallback(() => {
    onFilterChange((previous) => ({
      ...previous,
      months: new Set(Array.from({ length: 12 }, (_, i) => i + 1)),
    }));
  }, [onFilterChange]);

  const clearAllMonths = useCallback(() => {
    onFilterChange((previous) => ({ ...previous, months: new Set() }));
  }, [onFilterChange]);

  const monthSummary = useMemo(
    () => formatMonthSummary(filters.months, monthLabels, t("all_year")),
    [filters.months, monthLabels, t],
  );

  const renderSectionHeader = (
    section: SidebarSection,
    label: string,
    trailing?: ReactNode,
    summary?: string,
  ) => {
    const isOpen = openSections.has(section);
    return (
      <div className="flex min-h-11 items-center gap-2">
        <button
          type="button"
          onClick={() => toggleSection(section)}
          className="flex min-h-11 min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 text-left transition-colors hover:bg-flima-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-1 motion-reduce:transition-none"
          aria-expanded={isOpen}
          aria-controls={sectionBodyIds[section]}
        >
          <span className="min-w-0">
            <span className="block font-display text-sm font-semibold text-flima-spruce">
              {label}
            </span>
            {summary ? (
              <span className="mt-0.5 block truncate text-xs font-medium text-flima-slate/60">
                {summary}
              </span>
            ) : null}
          </span>
          <svg
            viewBox="0 0 20 20"
            className={`h-4 w-4 shrink-0 text-flima-river transition-transform motion-reduce:transition-none ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m5 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {isOpen && trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
    );
  };

  const speciesOpen = openSections.has("species");
  const monthsOpen = openSections.has("months");
  const measuresOpen = openSections.has("measures");
  const legendInfoRows = [
    [t("legend_info_species_overview"), t("legend_info_species_overview_result")],
    [t("legend_info_standalone_overview"), t("legend_info_standalone_overview_result")],
    [t("legend_info_measure_detail"), t("legend_info_measure_detail_result")],
    [t("legend_info_no_work_helm"), t("legend_info_no_work_helm_result")],
    [t("legend_info_no_work_bach"), t("legend_info_no_work_bach_result")],
    [t("legend_info_focus_fade"), t("legend_info_focus_fade_result")],
  ];
  const measuresHeaderTrailing = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setLegendInfoOpen((previous) => !previous)}
        aria-label={t("legend_info_toggle")}
        aria-expanded={legendInfoOpen}
        aria-controls="planning-legend-info"
        title={t("legend_info_toggle")}
        className={`flex h-9 w-9 items-center justify-center rounded-full border font-utility text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none ${
          legendInfoOpen
            ? "border-flima-river bg-flima-river text-white"
            : "border-flima-spruce/20 bg-white text-flima-slate/65 hover:border-flima-river hover:text-flima-spruce"
        }`}
      >
        i
      </button>
      {mapZoom >= ZOOM_BREAKPOINTS.overviewMax ? (
        <button
          type="button"
          onClick={onResetMeasureColors}
          className="min-h-9 shrink-0 rounded px-2 font-utility text-[0.62rem] uppercase tracking-wide text-flima-river transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
        >
          {t("reset_colors")}
        </button>
      ) : null}
    </div>
  );

  return (
    <aside
      ref={asideRef}
      // z-40 applies at every breakpoint (no md:z-auto): the translate-x-*
      // utility below is always active, even as translate-x-0, and any
      // element with a `transform` always creates a stacking context
      // regardless of z-index. On desktop that context previously fell back
      // to z-auto, tying it with the map (also z-auto) and losing the tie on
      // DOM order — so popovers opening from inside the sidebar (e.g. the
      // species info popover, which needs more width than the sidebar and
      // spills into the map's area) rendered behind the map.
      className={`absolute inset-y-0 left-0 z-40 flex w-[min(21rem,calc(100vw-2rem))] shrink-0 flex-col overflow-y-auto overscroll-contain border-r border-flima-spruce/10 bg-white/95 shadow-panel backdrop-blur transition-transform duration-200 md:relative md:w-72 md:translate-x-0 md:shadow-none lg:w-80 motion-reduce:transition-none ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex items-start justify-between gap-2 border-b border-flima-spruce/10 px-5 py-5">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold text-flima-spruce">
            {t("filters")}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-flima-slate/55 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river md:hidden motion-reduce:transition-none"
          aria-label={t("sidebar_toggle")}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <section
        data-tour-id="sidebar-species"
        className="border-b border-flima-spruce/10 px-5 py-3"
      >
        {renderSectionHeader("species", t("species"))}
        <div id={sectionBodyIds.species} hidden={!speciesOpen} className="mt-2 space-y-1.5">
          {config.species.map((species) => (
            <div
              key={species.id}
              className="flex min-h-11 items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-flima-mist focus-within:bg-flima-mist motion-reduce:transition-none"
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={filters.species.has(species.id)}
                  onChange={() => toggleSpecies(species.id)}
                  className={checkboxClassName}
                />
                {mode === "habitats" ? (
                  <span
                    className="h-2.5 w-7 shrink-0 rounded-full"
                    style={{ backgroundColor: species.color }}
                    aria-hidden="true"
                  />
                ) : null}
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-tight text-flima-slate">
                    {speciesName(species.id, species.name)}
                  </span>
                  <span className="block truncate text-xs italic text-flima-slate/60">
                    {species.latin}
                  </span>
                </span>
              </label>
              <SpeciesInfoPopover species={species} />
            </div>
          ))}
          {mode === "habitats" && hasSpeciesOverlap ? (
            <div className="flex min-h-11 items-center justify-center gap-3 rounded-lg px-2 py-2">
              <span
                className="h-2.5 w-7 shrink-0 rounded-full"
                style={{ backgroundColor: config.overlap_colors.both }}
                aria-hidden="true"
              />
              <span className="text-sm font-semibold leading-tight text-flima-slate">
                {t("both_species")}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      {mode === "planning" ? (
        <div data-tour-id="sidebar-planning-filters">
          <section className="border-b border-flima-spruce/10 px-5 py-3">
            {renderSectionHeader(
              "months",
              t("months"),
              undefined,
              monthsOpen ? undefined : monthSummary,
            )}
            <div id={sectionBodyIds.months} hidden={!monthsOpen} className="mt-3">
              <div className="grid grid-cols-4 gap-1.5">
                {monthLabels.map((label, index) => {
                  const month = index + 1;
                  const active = filters.months.has(month);
                  return (
                    <button
                      key={month}
                      type="button"
                      onClick={() => toggleMonth(month)}
                      aria-pressed={active}
                      className={`relative min-h-11 rounded-md border px-1.5 py-2 font-utility text-[0.68rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none ${
                        active
                          ? "border-flima-river bg-flima-river text-white hover:bg-flima-spruce"
                          : "border-flima-spruce/15 bg-white text-flima-slate/70 hover:border-flima-river hover:text-flima-spruce"
                      }`}
                    >
                      {active ? (
                        <svg
                          viewBox="0 0 20 20"
                          className="absolute right-0.5 top-0.5 h-3 w-3 text-white"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L4.3 10.7a1 1 0 1 1 1.4-1.4l2.8 2.8 6.8-6.8a1 1 0 0 1 1.4 0Z" />
                        </svg>
                      ) : null}
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={selectAllMonths}
                  className="min-h-9 rounded px-2 font-utility text-[0.62rem] uppercase tracking-wide text-flima-river transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
                >
                  {t("select_all")}
                </button>
                <span className="text-flima-spruce/15" aria-hidden="true">
                  |
                </span>
                <button
                  type="button"
                  onClick={clearAllMonths}
                  className="min-h-9 rounded px-2 font-utility text-[0.62rem] uppercase tracking-wide text-flima-river transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
                >
                  {t("clear_all")}
                </button>
              </div>
            </div>
          </section>

          {hasNoWorkAreas ? (
            <section className="border-b border-flima-spruce/10 px-5 py-3">
              <div
                className={`flex min-h-10 items-center gap-2 rounded-lg px-2 transition-colors ${
                  highlightNoWork ? "bg-flima-river/15 ring-2 ring-flima-river/40" : ""
                }`}
              >
                <span
                  className="h-0 w-7 shrink-0 border-t-[3px] border-dashed"
                  style={{ borderColor: "#DC2626" }}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  onClick={onNoWorkHighlightToggle}
                  className="min-w-0 flex-1 text-left text-sm font-medium leading-snug text-flima-slate hover:text-flima-spruce"
                >
                  {t("no_work_permitted")}
                </button>
                <button
                  type="button"
                  onClick={onNoWorkOverlayToggle}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-1 motion-reduce:transition-none ${
                    lineOverlayNoWork
                      ? "border-flima-river bg-flima-river text-white"
                      : "border-flima-spruce/20 bg-white text-flima-slate/60 hover:border-flima-river hover:text-flima-spruce"
                  }`}
                  aria-label={`${t("toggle_line")}: ${t("no_work_permitted")}`}
                  aria-pressed={lineOverlayNoWork}
                  title={t("toggle_line")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    aria-hidden="true"
                  >
                    <path d="M3 12c3-4 6-4 9 0s6 4 9 0" />
                  </svg>
                </button>
              </div>
            </section>
          ) : null}

          <section className="border-b border-flima-spruce/10 px-5 py-3">
            {renderSectionHeader(
              "measures",
              t("measures"),
              measuresHeaderTrailing,
            )}
            {measuresOpen && legendInfoOpen ? (
              <div
                id="planning-legend-info"
                className="mt-2 rounded-lg border border-flima-spruce/10 bg-flima-mist/45 p-3 text-xs text-flima-slate"
              >
                <p className="font-display text-sm font-semibold text-flima-spruce">
                  {t("legend_info_title")}
                </p>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full border-separate border-spacing-y-1 text-left">
                    <thead className="font-utility text-[0.62rem] uppercase tracking-[0.08em] text-flima-slate/55">
                      <tr>
                        <th scope="col" className="pr-2 font-semibold">
                          {t("legend_info_condition")}
                        </th>
                        <th scope="col" className="pl-2 font-semibold">
                          {t("legend_info_display")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="align-top text-[0.72rem] leading-snug">
                      {legendInfoRows.map(([condition, display]) => (
                        <tr key={condition}>
                          <td className="rounded-l-md bg-white/75 px-2 py-1.5 font-medium text-flima-slate">
                            {condition}
                          </td>
                          <td className="rounded-r-md bg-white/75 px-2 py-1.5 text-flima-slate/75">
                            {display}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            <div
              id={sectionBodyIds.measures}
              hidden={!measuresOpen}
              className="mt-2 space-y-1 [content-visibility:auto] [contain-intrinsic-size:0_28rem]"
            >
              {/* When zoomed out: show species colours as the legend */}
              {mapZoom < ZOOM_BREAKPOINTS.overviewMax ? (
                config.species
                  .filter((species) => filters.species.has(species.id))
                  .map((species) => (
                    <div
                      key={species.id}
                      className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-2"
                    >
                      <span
                        className="h-2.5 w-7 shrink-0 rounded-full"
                        style={{ backgroundColor: species.color }}
                        aria-hidden="true"
                      />
                      <span className="text-sm leading-snug text-flima-slate">
                        {species.name}
                      </span>
                    </div>
                  ))
              ) : null}
              {mapZoom < ZOOM_BREAKPOINTS.overviewMax && filters.species.size === 0
                ? visibleMeasures
                    .filter((measure) => !measure.species)
                    .map((measure) => (
                      <div
                        key={measure.id}
                        className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-2"
                      >
                        <span
                          className="h-2.5 w-7 shrink-0 rounded-full"
                          style={{ backgroundColor: measure.color }}
                          aria-hidden="true"
                        />
                        <span className="text-sm leading-snug text-flima-slate">
                          {measure.label}
                        </span>
                      </div>
                    ))
                : null}
              {mapZoom < ZOOM_BREAKPOINTS.overviewMax && hasSpeciesOverlap ? (
                <div className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-2">
                  <span
                    className="h-2.5 w-7 shrink-0 rounded-full"
                    style={{ backgroundColor: config.overlap_colors.both }}
                    aria-hidden="true"
                  />
                  <span className="text-sm leading-snug text-flima-slate">
                    {t("both_species")}
                  </span>
                </div>
              ) : null}
              {/* When zoomed in: show individual measure colours */}
              {mapZoom >= ZOOM_BREAKPOINTS.overviewMax
                ? visibleMeasures.map((measure) => {
                    const isHighlighted = highlightedMeasureIds.has(measure.id);
                    const hasLineOverlay = lineOverlayMeasureId === measure.id;
                    return (
                      <div
                        key={measure.id}
                        className={`flex min-h-10 items-center gap-2 rounded-lg px-2 py-2 transition-colors ${
                          isHighlighted
                            ? "bg-flima-river/15 ring-2 ring-flima-river/40"
                            : ""
                        }`}
                      >
                        {/* Colour wheel — pick your own colour for this measure */}
                        <ColorWheelPicker
                          color={measure.color}
                          onChange={(hex) => onMeasureColorChange(measure.id, hex)}
                          label={`${t("pick_measure_color")}: ${measure.label}`}
                          highlighted={isHighlighted}
                        />
                        {/* Label — click also highlights */}
                        <button
                          type="button"
                          onClick={() => onLegendMeasureClick(measure.id)}
                          className="min-w-0 flex-1 text-left text-sm leading-snug text-flima-slate hover:text-flima-spruce"
                        >
                          {measure.label}
                        </button>
                        {/* Line overlay toggle — draws a coloured line for this measure */}
                        <button
                          type="button"
                          onClick={() => onLineOverlayToggle(measure.id)}
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-1 motion-reduce:transition-none ${
                            hasLineOverlay
                              ? "border-flima-river bg-flima-river text-white"
                              : "border-flima-spruce/20 bg-white text-flima-slate/60 hover:border-flima-river hover:text-flima-spruce"
                          }`}
                          aria-label={`${t("toggle_line")}: ${measure.label}`}
                          aria-pressed={hasLineOverlay}
                          title={t("toggle_line")}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            aria-hidden="true"
                          >
                            <path d="M3 12c3-4 6-4 9 0s6 4 9 0" />
                          </svg>
                        </button>
                      </div>
                    );
                  })
                : null}
              {visibleMeasures.length === 0 && mapZoom >= ZOOM_BREAKPOINTS.overviewMax ? (
                <p className="px-2 py-3 text-sm text-flima-slate/60">{t("no_data")}</p>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </aside>
  );
});
